import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import adsApi from './adsApi';

const CAMPAIGN_TYPES = [
  'hero_banner',
  'mid_banner',
  'bottom_banner',
  'search_banner',
  'sponsored_listing',
  'offer_card',
  'coupon_card',
  'popup',
  'sticky_bottom',
  'floating_cta',
  'brand_landing',
  'seasonal',
];

const emptyCampaign = {
  name: '',
  advertiser_id: '',
  campaign_type: 'mid_banner',
  priority: 100,
  budget_amount: '',
  budget_currency: 'INR',
  start_at: '',
  end_at: '',
  timezone: 'Asia/Kolkata',
  status: 'draft',
  landing_page_url: '',
  internal_landing_slug: '',
  cta_text: 'Know More',
  cta_url: '',
  is_sponsored: false,
  placementCodes: ['home_mid'],
  pages: ['homepage'],
  locations: [],
  targeting: { devices: [], browsers: [], languages: [], auth_states: [] },
  banners: [
    {
      desktop_image_url: '',
      mobile_image_url: '',
      tablet_image_url: '',
      video_url: '',
      headline: '',
      subheadline: '',
      feature_text: '',
      cta_text: 'Know More',
      cta_url: '',
    },
  ],
  pricing: [{ pricing_model: 'flat', amount: 0, currency: 'INR' }],
};

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AdsCampaignsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adsApi.listCampaigns({ status: status || undefined, limit: 200 });
      setItems(res?.data?.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (fn, id) => {
    try {
      await fn(id);
      await load();
    } catch (err) {
      setError(err.message || 'Action failed');
    }
  };

  if (loading) return <LoadingSpinner size="large" message="Loading campaigns..." />;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Ad Campaigns</h1>
        <Link
          to="/ads/campaigns/new"
          className="rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white"
        >
          Create campaign
        </Link>
      </div>
      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}
      <select
        className="rounded border border-slate-300 px-3 py-2 text-sm"
        value={status}
        onChange={(e) => setStatus(e.target.value)}
      >
        <option value="">All statuses</option>
        {['draft', 'scheduled', 'active', 'paused', 'expired', 'archived'].map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Advertiser</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Priority</th>
              <th className="px-3 py-2">Dates</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-medium">
                  <Link className="text-blue-700 hover:underline" to={`/ads/campaigns/${row.id}`}>
                    {row.name}
                  </Link>
                </td>
                <td className="px-3 py-2">{row.advertiser_name}</td>
                <td className="px-3 py-2">{row.campaign_type}</td>
                <td className="px-3 py-2">{row.status}</td>
                <td className="px-3 py-2">{row.priority}</td>
                <td className="px-3 py-2 text-xs text-slate-600">
                  {new Date(row.start_at).toLocaleDateString()} → {new Date(row.end_at).toLocaleDateString()}
                </td>
                <td className="px-3 py-2 space-x-2 whitespace-nowrap">
                  {row.status === 'paused' ? (
                    <button type="button" className="text-green-700 hover:underline" onClick={() => act(adsApi.resumeCampaign, row.id)}>
                      Resume
                    </button>
                  ) : (
                    <button type="button" className="text-amber-700 hover:underline" onClick={() => act(adsApi.pauseCampaign, row.id)}>
                      Pause
                    </button>
                  )}
                  <button type="button" className="text-blue-700 hover:underline" onClick={() => act(adsApi.duplicateCampaign, row.id)}>
                    Duplicate
                  </button>
                  <button
                    type="button"
                    className="text-red-600 hover:underline"
                    onClick={() => {
                      if (window.confirm('Delete campaign?')) act(adsApi.deleteCampaign, row.id);
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {!items.length ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  No campaigns
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

